/**
 * TimeFilter — sparkline + brush.
 *
 * A single full-width sparkline of photos-per-bucket spanning the
 * loaded time range, with two draggable handles defining the active
 * filter. Replaces the prior glowing-segments timeline + per-day event
 * pills (felt complicated, mixed two visual idioms).
 *
 * Design source: tank-redesign Statistics sparkline pattern; brush
 * interaction is local to this component.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useTimeEvents } from "../../hooks/useTimeEvents";

const VIEW_W = 1000;
const VIEW_H = 80;
const HANDLE_HIT = 18; // px — pointer hit area around each handle

function formatBucketLabel(ts, bucketMs) {
  const d = new Date(ts);
  const dateStr = d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  if (bucketMs >= 24 * 60 * 60 * 1000) return dateStr;
  const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${dateStr} · ${timeStr}`;
}

function formatAxisLabel(ts) {
  return new Date(ts)
    .toLocaleDateString([], { day: "numeric", month: "short" })
    .toUpperCase();
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function Sparkline({ buckets, maxCount, range, totalStart, totalEnd, onChange, bucketMs }) {
  const svgRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null); // bucket index under pointer
  // Drag state. Two kinds:
  //   { kind: 'dot', anchorIdx, currentIdx } — picking a range from one dot to another
  //   { kind: 'start' | 'end' | 'move', anchorX, range0 } — manipulating an existing range
  const [drag, setDrag] = useState(null);

  const span = Math.max(1, totalEnd - totalStart);
  const n = buckets.length;
  const idxToX = useCallback(
    (i) => (i / Math.max(1, n - 1)) * VIEW_W,
    [n],
  );
  const tsToX = useCallback(
    (ts) => ((ts - totalStart) / span) * VIEW_W,
    [totalStart, span],
  );
  const xToTs = useCallback(
    (x) => totalStart + (clamp(x, 0, VIEW_W) / VIEW_W) * span,
    [totalStart, span],
  );
  const xToIdx = useCallback(
    (x) => clamp(Math.round((x / VIEW_W) * Math.max(1, n - 1)), 0, n - 1),
    [n],
  );
  const idxToTs = useCallback(
    (i) => buckets[clamp(i, 0, n - 1)]?.ts ?? totalStart,
    [buckets, n, totalStart],
  );

  const snapTs = useCallback((ts) => {
    if (!bucketMs) return ts;
    return Math.round((ts - totalStart) / bucketMs) * bucketMs + totalStart;
  }, [bucketMs, totalStart]);

  // Build the line path + gradient-fill area.
  const { linePath, areaPath } = useMemo(() => {
    if (!n || maxCount <= 0) return { linePath: "", areaPath: "" };
    const yScale = (c) => VIEW_H - 2 - (c / maxCount) * (VIEW_H - 6);
    const points = buckets.map((b, i) => [idxToX(i), yScale(b.count)]);
    const line = points
      .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
      .join(" ");
    const area = `${line} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z`;
    return { linePath: line, areaPath: area };
  }, [buckets, n, maxCount, idxToX]);

  const startX = range ? tsToX(range.start) : null;
  const endX = range ? tsToX(range.end) : null;
  const hasRange = startX != null && endX != null;

  const localX = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return clamp(((e.clientX - rect.left) / rect.width) * VIEW_W, 0, VIEW_W);
  };

  // Start a dot-anchored drag. anchorIdx = the bucket the pointer went down on.
  const startDotDrag = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ kind: "dot", anchorIdx: idx, currentIdx: idx });
    // Single-click/dot tap → emit a 1-bucket range immediately so the
    // user sees feedback without having to drag.
    const start = idxToTs(idx);
    const end = bucketMs ? start + bucketMs : start;
    onChange({ start, end });
    e.currentTarget?.setPointerCapture?.(e.pointerId);
  };

  // Existing-range manipulation (region drag, edge handles).
  const startRegionDrag = (e, kind) => {
    if (!hasRange) return;
    e.preventDefault();
    e.stopPropagation();
    const x = localX(e);
    if (x == null) return;
    setDrag({ kind, anchorX: x, range0: range });
    e.currentTarget?.setPointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const x = localX(e);
      if (x == null) return;
      if (drag.kind === "dot") {
        const idx = xToIdx(x);
        if (idx !== drag.currentIdx) setDrag({ ...drag, currentIdx: idx });
        const a = Math.min(drag.anchorIdx, idx);
        const b = Math.max(drag.anchorIdx, idx);
        const start = idxToTs(a);
        const end = bucketMs ? idxToTs(b) + bucketMs : idxToTs(b);
        onChange({ start, end });
        return;
      }
      const ts = snapTs(xToTs(x));
      const { kind, anchorX, range0 } = drag;
      if (!range0) return;
      if (kind === "start") {
        let s = ts;
        let t = range0.end;
        if (s > t) [s, t] = [t, s];
        onChange({ start: s, end: t });
      } else if (kind === "end") {
        let s = range0.start;
        let t = ts;
        if (s > t) [s, t] = [t, s];
        onChange({ start: s, end: t });
      } else if (kind === "move") {
        const delta = ts - snapTs(xToTs(anchorX));
        const dur = range0.end - range0.start;
        let s = range0.start + delta;
        s = clamp(s, totalStart, totalEnd - dur);
        onChange({ start: s, end: s + dur });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, bucketMs, idxToTs, onChange, snapTs, totalStart, totalEnd, xToIdx, xToTs]);

  const onSvgPointerMove = (e) => {
    if (drag && drag.kind !== "dot") return;
    const x = localX(e);
    if (x == null || !n) return;
    setHoverIdx(xToIdx(x));
  };

  const onSvgPointerLeave = () => {
    if (!drag) setHoverIdx(null);
  };

  // Determine which range bucket indices fall inside the active brush
  // so we can dim dots outside the selection.
  const inRange = (i) => {
    if (!hasRange) return false;
    const bucketTs = buckets[i]?.ts;
    if (bucketTs == null) return false;
    return bucketTs >= range.start && bucketTs <= range.end;
  };

  return (
    <div className="tf-spark" onPointerLeave={onSvgPointerLeave}>
      <div className="tf-spark-canvas">
      <svg
        ref={svgRef}
        className="tf-spark-svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        onPointerMove={onSvgPointerMove}
      >
        <defs>
          <linearGradient id="tf-spark-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(104,255,154,0.45)" />
            <stop offset="100%" stopColor="rgba(104,255,154,0)" />
          </linearGradient>
        </defs>

        {/* Faint baseline */}
        <line x1="0" x2={VIEW_W} y1={VIEW_H - 1} y2={VIEW_H - 1} className="tf-spark-baseline" />

        {/* Filled area + line */}
        {areaPath && <path d={areaPath} className="tf-spark-area" fill="url(#tf-spark-fill)" />}
        {linePath && (
          <path
            d={linePath}
            className={clsx("tf-spark-line", hasRange && "tf-spark-line--dim")}
            fill="none"
          />
        )}

        {/* Active range overlay */}
        {hasRange && (
          <g>
            <rect
              className="tf-spark-region"
              x={Math.min(startX, endX)}
              y={0}
              width={Math.max(1, Math.abs(endX - startX))}
              height={VIEW_H}
              onPointerDown={(e) => startRegionDrag(e, "move")}
            />
            <line className="tf-spark-region-edge" x1={startX} x2={startX} y1={0} y2={VIEW_H} />
            <line className="tf-spark-region-edge" x1={endX} x2={endX} y1={0} y2={VIEW_H} />
            {/* Handle hit-area + visual */}
            {[{ x: startX, kind: "start" }, { x: endX, kind: "end" }].map(({ x, kind }) => (
              <g key={kind}>
                <rect
                  className="tf-spark-handle-hit"
                  x={x - HANDLE_HIT / 2}
                  y={0}
                  width={HANDLE_HIT}
                  height={VIEW_H}
                  onPointerDown={(e) => startRegionDrag(e, kind)}
                />
                <line className="tf-spark-handle" x1={x} x2={x} y1={0} y2={VIEW_H} />
              </g>
            ))}
          </g>
        )}

        {/* Hover marker — vertical dashed line at hovered bucket */}
        {hoverIdx != null && (!drag || drag.kind === "dot") && (
          <line
            className="tf-spark-hover"
            x1={idxToX(hoverIdx)}
            x2={idxToX(hoverIdx)}
            y1={0}
            y2={VIEW_H}
          />
        )}
      </svg>

      {/* Dots — HTML overlay so they stay round regardless of the SVG's
          preserveAspectRatio="none" stretch. Each dot is positioned via
          percent-x and pixel-y. */}
      {n > 0 && maxCount > 0 && (
        <div className="tf-spark-dots" aria-hidden={false}>
          {buckets.map((b, i) => {
            const xPct = (i / Math.max(1, n - 1)) * 100;
            const yPct = (VIEW_H - 2 - (b.count / Math.max(1, maxCount)) * (VIEW_H - 6)) / VIEW_H * 100;
            const isHover = hoverIdx === i;
            const isInRange = inRange(i);
            return (
              <button
                key={i}
                type="button"
                className={clsx(
                  "tf-spark-dot",
                  isHover && "tf-spark-dot--hover",
                  isInRange && "tf-spark-dot--active",
                  hasRange && !isInRange && "tf-spark-dot--dim",
                )}
                style={{ left: `${xPct}%`, top: `${yPct}%` }}
                onPointerDown={(e) => startDotDrag(e, i)}
                onPointerEnter={() => !drag && setHoverIdx(i)}
                aria-label={`${formatBucketLabel(b.ts, bucketMs)} · ${b.count} photos`}
              />
            );
          })}
        </div>
      )}
      </div>

      {/* Hover tooltip — sits above the hovered bucket dot */}
      {hoverIdx != null && (!drag || drag.kind === "dot") && buckets[hoverIdx] && (
        <div
          className="tf-spark-tooltip"
          style={{ left: `calc(${(idxToX(hoverIdx) / VIEW_W) * 100}% - 0px)` }}
        >
          <span className="tf-spark-tooltip-when">
            {formatBucketLabel(buckets[hoverIdx].ts, bucketMs)}
          </span>
          <span className="tf-spark-tooltip-count">
            {buckets[hoverIdx].count} {buckets[hoverIdx].count === 1 ? "photo" : "photos"}
          </span>
        </div>
      )}

      {/* Axis labels */}
      <div className="tf-spark-axis">
        <span>{formatAxisLabel(totalStart)}</span>
        {totalEnd - totalStart > 24 * 60 * 60 * 1000 && (
          <span>{formatAxisLabel(totalStart + (totalEnd - totalStart) / 2)}</span>
        )}
        <span>{formatAxisLabel(totalEnd)}</span>
      </div>

      {/* UX hint — only shown when no range is active. */}
      {!hasRange && (
        <div className="tf-spark-hint">Drag any dot → dot to pick a range</div>
      )}
    </div>
  );
}

function TimeFilter({ photos, timeRange, onChange, onClear }) {
  const timeEvents = useTimeEvents(photos);
  const noTimestampSelected = timeRange?.noTimestamp === true;

  const handleNoTimestampClick = useCallback(() => {
    if (noTimestampSelected) onClear();
    else onChange({ noTimestamp: true });
  }, [noTimestampSelected, onChange, onClear]);

  const handleSparklineChange = useCallback(
    ({ start, end }) => onChange({ start, end }),
    [onChange],
  );

  // No photos with timestamps yet — keep the existing fallback affordance.
  if (!timeEvents.totalSpan || timeEvents.buckets.length === 0) {
    if (timeEvents.photosWithoutTime > 0) {
      return (
        <div className="time-filter-v2">
          <button
            className={clsx("no-time-indicator clickable", noTimestampSelected && "selected")}
            onClick={handleNoTimestampClick}
            type="button"
          >
            <span className="no-time-icon">[--:--]</span>
            <span>All {timeEvents.photosWithoutTime} photos lack capture timestamps</span>
          </button>
        </div>
      );
    }
    return null;
  }

  const range =
    timeRange && timeRange.start != null && timeRange.end != null
      ? { start: timeRange.start, end: timeRange.end }
      : null;

  const hasRange = !!range;

  return (
    <div className="time-filter-v2">
      <div className="tf-header">
        <span className="tf-header-eyebrow">Time</span>
        {hasRange && (
          <button type="button" className="tf-clear" onClick={onClear}>
            × Clear
          </button>
        )}
      </div>
      <Sparkline
        buckets={timeEvents.buckets}
        maxCount={timeEvents.maxBucketCount}
        bucketMs={timeEvents.bucketMs}
        totalStart={timeEvents.totalSpan.start}
        totalEnd={timeEvents.totalSpan.end}
        range={range}
        onChange={handleSparklineChange}
        onClear={onClear}
      />
      {timeEvents.photosWithoutTime > 0 && (
        <button
          className={clsx("no-time-indicator clickable", noTimestampSelected && "selected")}
          onClick={handleNoTimestampClick}
          type="button"
        >
          <span className="no-time-icon">[!]</span>
          <span>+{timeEvents.photosWithoutTime} photos without timestamp</span>
        </button>
      )}
    </div>
  );
}

export default TimeFilter;
